import React, { useEffect, useRef, useState } from "react";

/**
 * Animates a number from 0 → value with an ease-out curve.
 * CSS-only requestAnimationFrame; no external deps.
 */
const AnimatedNumber = ({ value, duration = 700, className, formatter }) => {
    const [display, setDisplay] = useState(0);
    const rafRef = useRef(null);
    const startRef = useRef(null);
    const fromRef = useRef(0);

    useEffect(() => {
        if (typeof value !== "number" || Number.isNaN(value)) {
            setDisplay(value);
            return;
        }
        cancelAnimationFrame(rafRef.current);
        fromRef.current = display || 0;
        startRef.current = null;
        const target = value;

        const step = (ts) => {
            if (startRef.current == null) startRef.current = ts;
            const elapsed = ts - startRef.current;
            const t = Math.min(1, elapsed / duration);
            // ease-out cubic
            const eased = 1 - Math.pow(1 - t, 3);
            const current = fromRef.current + (target - fromRef.current) * eased;
            setDisplay(current);
            if (t < 1) rafRef.current = requestAnimationFrame(step);
            else setDisplay(target);
        };
        rafRef.current = requestAnimationFrame(step);
        return () => cancelAnimationFrame(rafRef.current);
        // eslint-disable-next-line
    }, [value, duration]);

    const shown = typeof display === "number"
        ? (formatter ? formatter(display) : Math.round(display))
        : display;

    return <span className={className}>{shown}</span>;
};

export default AnimatedNumber;
